import { ChipTypeMap } from '@mui/material';
import Autocomplete, { AutocompleteProps } from '@mui/material/Autocomplete';
import { forwardRef } from 'react';
import { AutocompleteMuiAdapterProps } from './autocompleteMuiAdapter.types';

const AutocompleteMuiAdapterInner = <
  T,
  Multiple extends boolean | undefined,
  DisableClearable extends boolean | undefined,
  FreeSolo extends boolean | undefined,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
>(
  {
    getOptionSelected,
    renderOption: renderOptionOld,
    slotProps,
    ...otherProps
  }: AutocompleteMuiAdapterProps<T, Multiple, DisableClearable, FreeSolo, ChipComponent>,
  ref: React.Ref<unknown>,
) => {
  type MuiAutocompleteProps = AutocompleteProps<
    T,
    Multiple,
    DisableClearable,
    FreeSolo,
    ChipComponent
  >;

  const renderOptionNew: MuiAutocompleteProps['renderOption'] = renderOptionOld
    ? (liProps, option, state) => <li {...liProps}>{renderOptionOld(option, state)}</li>
    : undefined;

  const isOptionEqualToValue: MuiAutocompleteProps['isOptionEqualToValue'] = getOptionSelected
    ? getOptionSelected
    : undefined;

  const listBoxProps = {
    role: 'list-box',
    ...slotProps?.listbox,
  };

  return (
    <Autocomplete
      ref={ref}
      renderOption={renderOptionNew}
      isOptionEqualToValue={isOptionEqualToValue}
      slotProps={{
        ...slotProps,
        listbox: listBoxProps,
      }}
      {...otherProps}
    />
  );
};

const AutocompleteMuiAdapter = forwardRef(AutocompleteMuiAdapterInner) as <
  T,
  Multiple extends boolean | undefined = false,
  DisableClearable extends boolean | undefined = false,
  FreeSolo extends boolean | undefined = false,
  ChipComponent extends React.ElementType = ChipTypeMap['defaultComponent'],
>(
  props: AutocompleteMuiAdapterProps<T, Multiple, DisableClearable, FreeSolo, ChipComponent> &
    React.RefAttributes<unknown>,
) => JSX.Element;

export default AutocompleteMuiAdapter;
